import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { instanceHeaders } from "@/hooks/use-access";
import { apiDownload, apiRequestEnvelope } from "@/lib/api";
import { listQueryOptions } from "@/lib/listQuery";
import { logger } from "@/lib/logger";
import type { AuditAction } from "@shared/audit/actions";
import {
  AUDIT_ACTOR_HEADER,
  tenantAuditUnreadableInstanceSchema,
  type AuditEventView,
  type TenantAuditEventView,
  type TenantAuditUnreadableInstance,
} from "@shared/types/audit";
import {
  AUDIT_REPORTS,
  type AuditReportId,
  type AuditReportView,
} from "@shared/types/auditReports";

/**
 * The audit trail (FR-70, SEC-16, SEC-17, SEC-18).
 *
 * Two shapes share this module because they share the screen. `/audit/events` is
 * the free-form trail, filtered by actor, action, entity and date; a saved report
 * is a CLOSED action set the server chooses from the report id, so the screen
 * never sends the actions and a reviewer's evidence is reproducible.
 *
 * Pagination is by cursor, never by page number: the tables are append-only and
 * partitioned by month, so an offset would drift between two requests and the
 * page numbers would be lying. Walking back is therefore a stack of the cursors
 * already visited, kept here rather than in the grid.
 *
 * The ACTOR filter travels in a header and not in the query string. What the
 * reviewer types there is an email address, and a query string is written
 * verbatim into every ingress and proxy access log on the way — logs that are
 * not ours to scrub, which is the same reason the run socket carries its token
 * in a subprotocol. The server hashes it before it touches a row either way; the
 * point is the hop in between. Because the address is no longer part of the
 * path, it is named in the query KEY instead, or two different actors would
 * share one cache entry.
 */

export type AuditScope = "instance" | "platform";
/** `all` is the free-form trail; anything else is one of the seven SEC-16 reports. */
export type AuditView = "all" | AuditReportId;

export interface AuditFilters {
  actor?: string;
  action?: AuditAction | "";
  entityType?: string;
  /** ISO 8601, inclusive at both ends. */
  from?: string;
  to?: string;
}

function queryString(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

const PAGE_SIZE = 50;

export function auditEventsPath(
  scope: AuditScope,
  filters: AuditFilters,
  cursor: string | null,
): string {
  const base = scope === "platform" ? "/api/v2/platform/audit/events" : "/api/v2/audit/events";
  return `${base}${queryString({
    limit: PAGE_SIZE,
    cursor: cursor ?? undefined,
    action: filters.action || undefined,
    entityType: filters.entityType,
    from: filters.from,
    to: filters.to,
  })}`;
}

export function auditReportPath(
  scope: AuditScope,
  report: AuditReportId,
  filters: AuditFilters,
  cursor: string | null,
): string {
  const base =
    scope === "platform"
      ? `/api/v2/platform/audit/reports/${report}`
      : `/api/v2/audit/reports/${report}`;
  // A report decides its own actions and entity types, so only the date range
  // travels; sending an action would be a filter the route ignores.
  return `${base}${queryString({
    limit: PAGE_SIZE,
    cursor: cursor ?? undefined,
    from: filters.from,
    to: filters.to,
  })}`;
}

export function auditExportPath(scope: AuditScope, view: AuditView, filters: AuditFilters): string {
  if (view === "all") {
    const base =
      scope === "platform"
        ? "/api/v2/platform/audit/events/export.csv"
        : "/api/v2/audit/events/export.csv";
    return `${base}${queryString({
      action: filters.action || undefined,
      entityType: filters.entityType,
      from: filters.from,
      to: filters.to,
    })}`;
  }
  const base =
    scope === "platform"
      ? `/api/v2/platform/audit/reports/${view}/export.csv`
      : `/api/v2/audit/reports/${view}/export.csv`;
  return `${base}${queryString({ from: filters.from, to: filters.to })}`;
}

/**
 * The headers one audit request carries: the instance it is scoped to and the
 * actor being searched for. Built in one place so the grid, the report and both
 * CSV exports cannot drift apart on either.
 */
export function auditHeaders(
  instanceId: string | undefined,
  actor: string | undefined,
): Record<string, string> | undefined {
  const headers: Record<string, string> = { ...instanceHeaders(instanceId) };
  if (actor?.trim()) headers[AUDIT_ACTOR_HEADER] = actor.trim();
  return Object.keys(headers).length > 0 ? headers : undefined;
}

/** The cursor walk, kept out of the grid so Previous is a stack and not arithmetic. */
export function useCursorStack() {
  const [stack, setStack] = useState<Array<string | null>>([null]);
  const cursor = stack[stack.length - 1] ?? null;
  return {
    cursor,
    canGoBack: stack.length > 1,
    next: useCallback((nextCursor: string) => setStack((s) => [...s, nextCursor]), []),
    back: useCallback(() => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s)), []),
    reset: useCallback(() => setStack([null]), []),
  };
}

/** One page of the free-form trail, plus the cursor for the page after it. */
export function useAuditEvents(
  scope: AuditScope,
  filters: AuditFilters,
  cursor: string | null,
  enabled: boolean,
) {
  const { selectedInstance } = useTenantInstance();
  const path = auditEventsPath(scope, filters, cursor);
  const query = useQuery({
    ...listQueryOptions,
    // The actor is a header rather than part of the path, so it is named here or
    // two different actors would read each other's cached page.
    queryKey: [path, "events", filters.actor ?? "", selectedInstance?.id ?? null],
    enabled,
    queryFn: async ({ signal }) =>
      apiRequestEnvelope<AuditEventView[]>(path, {
        signal,
        headers: auditHeaders(selectedInstance?.id, filters.actor),
      }),
  });
  return {
    ...query,
    path,
    rows: useMemo(() => query.data?.data ?? [], [query.data]),
    nextCursor: query.data?.meta?.nextCursor ?? null,
  };
}

/**
 * A key nothing ever writes, for the report query while no report is chosen.
 *
 * A disabled TanStack query still READS its key, so standing in a real report id
 * would serve the grants report's cached page to a screen that is showing the
 * unfiltered trail — the wrong rows, with no request to explain them. The
 * sentinel is not a path, so no fetch can ever land on it.
 */
const NO_REPORT_KEY = "audit-report:none";

/**
 * Opening a report or downloading a CSV writes a row (`audit.review`, `history.export`,
 * `audit.export`), so a trail already read is out of date: B-51 (round 9) found the visit missing
 * from This instance until a reload. Every cached trail page is marked stale, and the one on screen
 * is read again.
 */
function invalidateTrails(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[1] === "events" || query.queryKey[1] === "tenant-events",
  });
}

/** One page of a saved review report (SEC-16). Opening it is audited server-side. */
export function useAuditReport(
  scope: AuditScope,
  /** Null while the screen is showing the unfiltered trail. */
  report: AuditReportId | null,
  filters: AuditFilters,
  cursor: string | null,
  enabled: boolean,
) {
  const { selectedInstance } = useTenantInstance();
  const queryClient = useQueryClient();
  const path = report ? auditReportPath(scope, report, filters, cursor) : null;
  const query = useQuery({
    ...listQueryOptions,
    queryKey: [path ?? NO_REPORT_KEY, "report", filters.actor ?? ""],
    enabled: enabled && path !== null,
    queryFn: async ({ signal }) => {
      // Unreachable: the query is disabled without a path. Throwing rather than
      // returning an empty page keeps a future caller from reading a made-up
      // report as if the server had answered it.
      if (!path) throw new Error("No report is selected");
      const page = await apiRequestEnvelope<AuditReportView>(path, {
        signal,
        headers: auditHeaders(selectedInstance?.id, filters.actor),
      });
      invalidateTrails(queryClient);
      return page;
    },
  });
  return {
    ...query,
    path,
    spec: report ? AUDIT_REPORTS[report] : null,
    // The order is the SERVER's: break-glass first for the grants report, then
    // newest first (SEC-18). Nothing here re-sorts it, and the report routes
    // take no sort parameter, so the grid cannot ask for another one.
    rows: useMemo(() => query.data?.data?.rows ?? [], [query.data]),
    nextCursor: query.data?.meta?.nextCursor ?? null,
  };
}

/**
 * The tenant trail's filters (product gaps row 9). `origin` is `tenant` (the rows about the
 * tenant itself) or an instance id; `actionPrefix` is the start of an action, `access.` for a
 * whole family. The actor is a header, as on every audit read.
 */
export interface TenantAuditFilters {
  actor?: string;
  origin?: string;
  actionPrefix?: string;
  from?: string;
  to?: string;
}

function tenantAuditQuery(filters: TenantAuditFilters) {
  return {
    origin: filters.origin || undefined,
    actionPrefix: filters.actionPrefix || undefined,
    from: filters.from,
    to: filters.to,
  };
}

export function tenantAuditEventsPath(
  tenantId: string,
  filters: TenantAuditFilters,
  cursor: string | null,
): string {
  return `/api/v2/tenants/${tenantId}/audit/events${queryString({
    limit: PAGE_SIZE,
    cursor: cursor ?? undefined,
    ...tenantAuditQuery(filters),
  })}`;
}

export function tenantAuditExportPath(tenantId: string, filters: TenantAuditFilters): string {
  return `/api/v2/tenants/${tenantId}/audit/events/export.csv${queryString(
    tenantAuditQuery(filters),
  )}`;
}

const NO_UNREADABLE: readonly TenantAuditUnreadableInstance[] = [];

/**
 * By id, name and status only, so an entry with a reason this client does not know is still
 * shown (the status, round 6 W10, is optional: an older server sends none).
 */
const unreadableEntrySchema = tenantAuditUnreadableInstanceSchema.pick({
  id: true,
  name: true,
  status: true,
  // Round 7: what the write-once archive holds of a store that is gone (optional).
  archive: true,
  // The storage-removed mark (optional): the warning says why from it, never from the status.
  storageRemovedAt: true,
  storageRemovedReason: true,
});

type UnreadableEntry = Pick<
  TenantAuditUnreadableInstance,
  "id" | "name" | "status" | "archive" | "storageRemovedAt" | "storageRemovedReason"
>;

function unreadableOf(meta: unknown): ReadonlyArray<UnreadableEntry> {
  const list = (meta as { unreadableInstances?: unknown } | undefined)?.unreadableInstances;
  if (!Array.isArray(list) || list.length === 0) return NO_UNREADABLE;
  return list.flatMap((entry) => {
    const parsed = unreadableEntrySchema.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  });
}

/**
 * One page of the tenant trail; the actor, when one is typed, travels as a header.
 * `unreadableInstances` names the instances whose own trail the server could not read for this
 * answer (`meta.unreadableInstances`); empty when every store was read, or from an older server.
 */
export function useTenantAuditEvents(
  tenantId: string,
  filters: TenantAuditFilters,
  cursor: string | null,
  enabled: boolean,
) {
  const path = tenantAuditEventsPath(tenantId, filters, cursor);
  const query = useQuery({
    ...listQueryOptions,
    queryKey: [path, "tenant-events", filters.actor ?? ""],
    enabled,
    queryFn: async ({ signal }) =>
      apiRequestEnvelope<TenantAuditEventView[]>(path, {
        signal,
        headers: auditHeaders(undefined, filters.actor),
      }),
  });
  return {
    ...query,
    path,
    rows: useMemo(() => query.data?.data ?? [], [query.data]),
    nextCursor: query.data?.meta?.nextCursor ?? null,
    unreadableInstances: useMemo(() => unreadableOf(query.data?.meta), [query.data]),
  };
}

/**
 * Export the current view as CSV.
 *
 * The route is audited — `audit.export` for the trail, `history.export` for a
 * report — so this is a real request with the session's headers, not an anchor
 * the browser follows anonymously.
 */
export function useAuditExport() {
  const { selectedInstance } = useTenantInstance();
  const queryClient = useQueryClient();
  const [isExporting, setExporting] = useState(false);

  const exportCsv = useCallback(
    async (path: string, actor?: string): Promise<string> => {
      setExporting(true);
      try {
        const { blob, filename } = await apiDownload(path, {
          headers: auditHeaders(selectedInstance?.id, actor),
          // #1110: an audit export needs a recent sign-in.
          stepUp: "export",
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
        logger.info("audit trail exported");
        invalidateTrails(queryClient);
        return filename;
      } finally {
        setExporting(false);
      }
    },
    [selectedInstance, queryClient],
  );

  return { exportCsv, isExporting };
}
